// frontend/src/components/MainGrid.js
import React from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import FolderRoundedIcon from '@mui/icons-material/FolderRounded';
import PendingActionsRoundedIcon from '@mui/icons-material/PendingActionsRounded';
import EventBusyRoundedIcon from '@mui/icons-material/EventBusyRounded';
import { useAuth } from '../contexts/AuthContext';
import useDashboard from '../hooks/useDashboard';
import BusinessStatCard from './BusinessStatCard';

function parseCount(value) {
  if (typeof value !== 'string') return Number(value || 0);
  const m = value.match(/\d+/);
  return m ? Number(m[0]) : 0;
}

export default function MainGrid() {
  const { role } = useAuth();
  const { data, loading } = useDashboard();

  if (loading || !data) return null;

  const activeCount = parseCount(data.stats?.[0]?.value);
  const totalCount = parseCount(data.stats?.[1]?.value);
  const pendingCount = Number(data.pendingApprovals || 0);
  const deadlineCount = (data.deadlines || []).length;
  const canSeePending = role === 'admin' || role === 'manager';

  return (
    <Box sx={{ width: '100%' }}>
      <Alert severity="info" variant="outlined" sx={{ mb: 3 }}>
        ダッシュボードでは、案件全体のサマリ・自分の担当案件・契約期限アラートを一覧で確認できます。
      </Alert>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <BusinessStatCard
            icon={<TrendingUpRoundedIcon />}
            title="進行中案件"
            value={`${activeCount} 件`}
            accent="primary"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <BusinessStatCard
            icon={<FolderRoundedIcon />}
            title="案件総数"
            value={`${totalCount} 件`}
            accent="primary"
          />
        </Grid>
        {canSeePending && (
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <BusinessStatCard
              icon={<PendingActionsRoundedIcon />}
              title="承認待ち"
              value={`${pendingCount} 件`}
              accent="warning"
            />
          </Grid>
        )}
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <BusinessStatCard
            icon={<EventBusyRoundedIcon />}
            title="期限間近の契約 (30日)"
            value={`${deadlineCount} 件`}
            accent="error"
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                自分の担当案件
              </Typography>
              {data.myProjects?.length ? (
                <Stack divider={<Divider flexItem />} spacing={1.5}>
                  {data.myProjects.map((p) => (
                    <Box
                      key={p.id}
                      onClick={() => (window.location.href = `/projects/${p.id}`)}
                      sx={{
                        cursor: 'pointer',
                        py: 0.5,
                        '&:hover': { bgcolor: 'action.hover', borderRadius: 1 },
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="body2" fontWeight={500} noWrap>
                          {p.title}
                        </Typography>
                        <Chip
                          label={p.status || '-'}
                          size="small"
                          color={p.status === '進行中' ? 'primary' : 'default'}
                          variant="outlined"
                        />
                      </Box>
                      {typeof p.progress === 'number' && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(100, Math.max(0, p.progress))}
                            sx={{ flex: 1, height: 6, borderRadius: 3 }}
                          />
                          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 32, textAlign: 'right' }}>
                            {p.progress}%
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  現在、担当中の案件はありません。
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                契約期限アラート
              </Typography>
              {data.deadlines?.length ? (
                <Stack divider={<Divider flexItem />} spacing={1.5}>
                  {data.deadlines.map((d) => {
                    const days = d.endDate
                      ? Math.ceil((new Date(d.endDate) - new Date()) / 86400000)
                      : null;
                    return (
                      <Box
                        key={d.id}
                        onClick={() => (window.location.href = `/contracts`)}
                        sx={{
                          cursor: 'pointer',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          py: 0.5,
                          '&:hover': { bgcolor: 'action.hover', borderRadius: 1 },
                        }}
                      >
                        <Box sx={{ minWidth: 0, mr: 2 }}>
                          <Typography variant="body2" fontWeight={500} noWrap>
                            {d.title || '-'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            終了: {d.endDate ? new Date(d.endDate).toLocaleDateString('ja-JP') : '-'}
                          </Typography>
                        </Box>
                        {days !== null && (
                          <Chip
                            size="small"
                            label={days < 0 ? `${Math.abs(days)}日超過` : `あと${days}日`}
                            color={days < 0 ? 'error' : days <= 7 ? 'warning' : 'default'}
                            variant={days < 0 ? 'filled' : 'outlined'}
                          />
                        )}
                      </Box>
                    );
                  })}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  30日以内に終了する契約はありません。
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
